import { supabase } from "@/integrations/supabase/client";
import { logger, serializeError } from "@/lib/logger";

export interface PastTeamSummary {
  team_id: number;
  team_name: string;
  /** ISO date_start of the most recent quizrun the team played. */
  last_used: string;
}

export interface RegistrationMember {
  person_id: number;
  first_name: string | null;
  last_name: string | null;
  primary_email: string | null;
  is_free: boolean;
  is_thinker: boolean;
  payment_status: import("@/api/payments").ParticipationPaymentStatus;
}

export type RegistrationCategory = "solo" | "team";

export interface MyEventRegistration {
  participation_id: number;
  category: RegistrationCategory;
  team_id: number | null;
  team_name: string | null;
  members: RegistrationMember[];
  can_edit: boolean;
}

export interface PersonSearchResult {
  person_id: number;
  first_name: string | null;
  last_name: string | null;
  primary_email: string | null;
  last_country: string | null;
}

/**
 * True iff the person has at least one participation across any event
 * (past or upcoming) — i.e. NOT a first-timer. Used to gate the
 * "first event's on us" reassurance so it never shows to returning users.
 */
export async function fetchHasAnyRegistration(
  personId: number,
): Promise<boolean> {
  const { count, error } = await supabase
    .from("participation_member")
    .select("person_id", { count: "exact", head: true })
    .eq("person_id", personId);
  if (error) throw error;
  return (count ?? 0) > 0;
}

/** Register the signed-in user solo for an event. Returns the participation id. */
export async function registerForEvent(eventId: number): Promise<number> {
  const { data, error } = await supabase.rpc("register_for_event", {
    p_event_id: eventId,
  });
  if (error) throw error;
  return data as number;
}

/** Cancel the signed-in user's solo registration. */
export async function cancelEventRegistration(
  eventId: number,
): Promise<boolean> {
  const { data, error } = await supabase.rpc("cancel_event_registration", {
    p_event_id: eventId,
  });
  if (error) throw error;
  return Boolean(data);
}

/**
 * Register or convert to a team registration. Provide exactly one of
 * `teamId` (must be a team the caller has been a member of before) or
 * `newTeamName` (creates a new team).
 */
export async function registerTeamForEvent(
  eventId: number,
  team: { teamId: number } | { newTeamName: string },
): Promise<number> {
  const { data, error } = await supabase.rpc("register_team_for_event", {
    p_event_id: eventId,
    p_team_id: "teamId" in team ? team.teamId : null,
    p_new_team_name: "newTeamName" in team ? team.newTeamName : null,
  });
  if (error) throw error;
  return data as number;
}

export async function addTeamMember(
  eventId: number,
  personId: number,
): Promise<number> {
  const { data, error } = await supabase.rpc("add_team_member", {
    p_event_id: eventId,
    p_person_id: personId,
  });
  if (error) throw error;
  return data as number;
}

export async function removeTeamMember(
  eventId: number,
  personId: number,
): Promise<boolean> {
  const { data, error } = await supabase.rpc("remove_team_member", {
    p_event_id: eventId,
    p_person_id: personId,
  });
  if (error) throw error;
  return Boolean(data);
}

/** Move a single member out of the caller's team into their own solo registration. */
export async function moveMemberToSolo(
  eventId: number,
  personId: number,
): Promise<number> {
  const { data, error } = await supabase.rpc("move_member_to_solo", {
    p_event_id: eventId,
    p_person_id: personId,
  });
  if (error) throw error;
  return data;
}

export async function cancelTeamRegistration(
  eventId: number,
): Promise<boolean> {
  const { data, error } = await supabase.rpc("cancel_team_registration", {
    p_event_id: eventId,
  });
  if (error) throw error;
  return Boolean(data);
}

/** Rename the team attached to the caller's event registration. */
export async function renameTeamForEvent(
  eventId: number,
  newName: string,
): Promise<boolean> {
  const { data, error } = await supabase.rpc("rename_team_for_event", {
    p_event_id: eventId,
    p_new_name: newName,
  });
  if (error) throw error;
  return Boolean(data);
}

export async function addNewPersonToTeam(
  eventId: number,
  firstName: string,
  lastName: string | undefined,
  email: string | undefined,
  gender: "female" | "male" | "other",
  homeCity: string | null,
  homeCountry: string | null,
): Promise<{ personId: number; memberId: number }> {
  const { data, error } = await supabase.rpc("add_new_person_to_team", {
    p_event_id: eventId,
    p_first_name: firstName,
    p_last_name: lastName ?? null,
    p_email: email?.trim().toLowerCase() || null,
    p_gender: gender,
    p_home_city: homeCity?.trim() || null,
    p_home_country: homeCountry?.trim() || null,
  });
  if (error) throw error;
  const result = data as { person_id: number; member_id: number };
  return { personId: result.person_id, memberId: result.member_id };
}

export async function searchPersonsForTeam(
  eventId: number,
  query: string,
): Promise<PersonSearchResult[]> {
  if (query.trim().length < 2) return [];
  const { data, error } = await supabase.rpc("search_persons_for_team", {
    p_event_id: eventId,
    p_query: query,
  });
  if (error) throw error;
  return (data ?? []) as PersonSearchResult[];
}

/**
 * Past teams the person has been a member of, most recent first.
 */
export async function fetchMyPastTeams(
  personId: number,
): Promise<PastTeamSummary[]> {
  const { data: rows, error } = await supabase
    .from("participation_member")
    .select("team_id, participation!inner(quizrun_id)")
    .eq("person_id", personId)
    .not("team_id", "is", null);
  if (error) throw error;

  const memberRows = (rows ?? []) as Array<{
    team_id: number | null;
    participation: { quizrun_id: number } | null;
  }>;

  // Read quizrun start dates from the view: base `quizrun` RLS is host/admin
  // only, so a regular user can't read it (an inner embed would drop every row).
  const quizrunIds = [
    ...new Set(
      memberRows
        .map((r) => r.participation?.quizrun_id)
        .filter((id): id is number => id != null),
    ),
  ];
  if (quizrunIds.length === 0) return [];

  const { data: quizruns, error: quizrunsErr } = await supabase
    .from("v_quizrun")
    .select("id, date_start")
    .in("id", quizrunIds);
  if (quizrunsErr) throw quizrunsErr;
  const dateByQuizrun = new Map<number, string>(
    (quizruns ?? []).map((q) => [q.id, q.date_start]),
  );

  const lastByTeam = new Map<number, string>();
  for (const r of memberRows) {
    if (r.team_id === null || r.team_id === undefined) continue;
    const quizrunId = r.participation?.quizrun_id;
    const dateStart =
      quizrunId != null ? dateByQuizrun.get(quizrunId) : undefined;
    if (!dateStart) continue;
    const prev = lastByTeam.get(r.team_id);
    if (!prev || dateStart > prev) lastByTeam.set(r.team_id, dateStart);
  }
  if (lastByTeam.size === 0) return [];

  const ids = [...lastByTeam.keys()];
  const { data: teams, error: teamsErr } = await supabase
    .from("team")
    .select("id, name")
    .in("id", ids);
  if (teamsErr) throw teamsErr;

  return (teams ?? [])
    .map((t) => ({
      team_id: t.id,
      team_name: t.name ?? "",
      last_used: lastByTeam.get(t.id) ?? "",
    }))
    .sort((a, b) =>
      a.last_used > b.last_used ? -1 : a.last_used < b.last_used ? 1 : 0,
    );
}

/**
 * The caller's current registration for an event (or null if not registered).
 */
export async function fetchMyEventRegistration(
  eventId: number,
  personId: number,
): Promise<MyEventRegistration | null> {
  const { data, error } = await supabase
    .from("participation_member")
    .select("team_id, participation!inner(id, event_id, category)")
    .eq("person_id", personId)
    .eq("participation.event_id", eventId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const participation = data.participation as {
    id: number;
    event_id: number | null;
    category: string;
  } | null;
  if (!participation) return null;

  const participationId = participation.id;
  const teamId = (data.team_id as number | null) ?? null;
  const isTeam = teamId !== null;

  const [membersRes, namesRes, teamRes, eventRes] = await Promise.all([
    supabase
      .from("v_member_payment")
      .select("person_id, is_free, payment_status")
      .eq("participation_id", participationId),
    // Names/gender come from a SECURITY DEFINER RPC: person RLS stays
    // self-scoped, so a person!inner embed would drop teammates' rows.
    supabase.rpc("get_team_member_names", {
      p_participation_id: participationId,
    }),
    isTeam
      ? supabase.from("team").select("name").eq("id", teamId!).maybeSingle()
      : Promise.resolve({ data: null, error: null } as const),
    // Read the finish date from the view, not the base `event` table (RLS on
    // the base table is admin-only for authenticated users).
    supabase
      .from("v_event")
      .select("date_finish")
      .eq("id", eventId)
      .maybeSingle(),
  ]);
  if (membersRes.error) throw membersRes.error;
  // get_team_member_names is a SECURITY DEFINER RPC added in 20260620. If the
  // frontend ships before that migration runs, only swallow the "function not
  // deployed" case (PGRST202 / 42883) and proceed with empty names rather than
  // crashing the whole profile registration view; any other error must surface.
  if (namesRes.error) {
    if (namesRes.error.code === "PGRST202" || namesRes.error.code === "42883") {
      logger.warn(
        "get_team_member_names missing (migration not applied?); names will be empty",
        serializeError(namesRes.error),
      );
    } else {
      throw namesRes.error;
    }
  }
  if (teamRes.error) throw teamRes.error;
  if (eventRes.error) throw eventRes.error;

  const memberRows = (membersRes.data ?? []) as Array<{
    person_id: number;
    is_free: boolean;
    payment_status: string | null;
  }>;
  const nameById = new Map<
    number,
    { first_name: string | null; last_name: string | null; is_thinker: boolean }
  >();
  for (const r of (namesRes.data ?? []) as Array<{
    person_id: number;
    first_name: string | null;
    last_name: string | null;
    is_thinker?: boolean;
  }>) {
    nameById.set(r.person_id, {
      first_name: r.first_name,
      last_name: r.last_name,
      is_thinker: r.is_thinker ?? false,
    });
  }
  const personIds = memberRows
    .map((m) => m.person_id)
    .filter((id): id is number => id !== null);

  const emailMap = new Map<number, string>();
  if (personIds.length > 0) {
    const { data: emails, error: emailsErr } = await supabase
      .from("person_email")
      .select("person_id, email, is_primary")
      .in("person_id", personIds);
    if (emailsErr) throw emailsErr;
    for (const row of emails ?? []) {
      const existing = emailMap.get(row.person_id);
      if (!existing || row.is_primary) emailMap.set(row.person_id, row.email);
    }
  }

  const members: RegistrationMember[] = memberRows.map((m) => ({
    person_id: m.person_id,
    first_name: nameById.get(m.person_id)?.first_name ?? null,
    last_name: nameById.get(m.person_id)?.last_name ?? null,
    primary_email: emailMap.get(m.person_id) ?? null,
    is_free: m.is_free ?? false,
    is_thinker: nameById.get(m.person_id)?.is_thinker ?? false,
    payment_status:
      (m.payment_status as import("@/api/payments").ParticipationPaymentStatus) ??
      null,
  }));

  const dateFinish = eventRes.data?.date_finish ?? null;
  // Editable while the event is current (ongoing) or upcoming; a finished event
  // is locked. Missing date (detached run) stays editable.
  const canEdit = dateFinish
    ? new Date(dateFinish).getTime() >= Date.now()
    : true;

  return {
    participation_id: participationId,
    category: isTeam ? "team" : "solo",
    team_id: teamId,
    team_name: (teamRes.data as { name: string | null } | null)?.name ?? null,
    members,
    can_edit: canEdit,
  };
}

// -----------------------------------------------------------------------------
// Friendly error mapping
// -----------------------------------------------------------------------------

const FRIENDLY_MESSAGES: Record<string, string> = {
  no_profile: "You need to complete your profile first.",
  event_finished: "This event has already finished.",
  event_not_found: "That event could not be found. It may have been removed.",
  forbidden:
    "You don't have permission to change this team, or the editing window has closed.",
  must_be_team: "This registration is solo. Switch to a team first.",
  has_results:
    "Cannot cancel — results have already been recorded for this team.",
  has_results_last_member:
    "Cannot remove the last member — a result is already recorded for this team.",
  has_results_cannot_merge:
    "This person already has recorded results in another participation. An admin needs to merge them.",
  team_name_taken:
    "A team with this name already exists. Try another name, or ask a current member to add you.",
  team_already_in_event: "This team is already registered for this event.",
  already_in_other_team:
    "You're already in a different team for this event. Cancel that first.",
  team_not_yours: "You can only re-use a team you've been a member of before.",
  team_full: "This team already has the maximum of 4 members.",
  not_alone_on_team:
    "Remove the other team members first, then switch to solo.",
  invalid_team_name: "Team name can't be empty.",
  already_participates:
    "You're already registered for this Quizrun (in another city).",
  invalid_first_name: "First name can't be empty.",
  invalid_last_name: "Last name can't be empty.",
  email_taken: "This email is already associated with another person.",
  terms_required: "Please accept the Terms & Waiver before registering.",
};

/** Map a Supabase RPC error to a user-friendly message. */
export function friendlyRegistrationError(
  err: unknown,
  fallback: string,
): string {
  let raw = "";
  if (err instanceof Error) {
    raw = err.message;
  } else if (err && typeof err === "object" && "message" in err) {
    raw = String((err as { message: unknown }).message ?? "");
  } else {
    raw = String(err ?? "");
  }
  // Longer keys first so `has_results_last_member` wins over `has_results`.
  const keys = Object.keys(FRIENDLY_MESSAGES).sort(
    (a, b) => b.length - a.length,
  );
  for (const key of keys) {
    if (raw.includes(`${key}:`) || raw.startsWith(key))
      return FRIENDLY_MESSAGES[key];
  }
  if (raw.includes("already participates"))
    return FRIENDLY_MESSAGES.already_participates;
  return fallback;
}
